// Utility for computing date ranges in TimeTrackings

/**
 * Union type for supported range computation modes
 */
export type RangeMode = 'thisWeek' | 'thisMonth' | 'lastMonth' | 'allTime' | 'custom';

/**
 * Interface for the date range result
 */
export interface DateRange {
  start: Date;
  end: Date;
}

/**
 * Computes start and end dates based on mode and optional custom range.
 *
 * @param mode - One of 'thisWeek','thisMonth','lastMonth','allTime','custom'
 * @param customStart - ISO date string YYYY-MM-DD for custom start
 * @param customEnd - ISO date string YYYY-MM-DD for custom end
 * @param now - Reference date for computing ranges (for testing)
 * @returns Object with start and end Date properties
 */
export function computeRange(
  mode: RangeMode,
  customStart?: string,
  customEnd?: string,
  now: Date = new Date()
): DateRange {
  let start = new Date('2000-01-01T00:00:00');
  let end = new Date('2100-01-01T00:00:00');

  if (mode === 'thisWeek') {
    const day = now.getDay(); // Sun=0, Mon=1
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    start = new Date(now);
    start.setDate(diff);
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(end.getDate() + 7);
  } else if (mode === 'thisMonth') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  } else if (mode === 'lastMonth') {
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    end = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (mode === 'allTime') {
    start = new Date('2000-01-01T00:00:00');
    end = new Date('2100-01-01T00:00:00');
  } else if (mode === 'custom') {
    if (customStart) {
      start = new Date(`${customStart}T00:00:00`);
    }
    if (customEnd) {
      const tmp = new Date(`${customEnd}T00:00:00`);
      end = new Date(tmp.getTime() + 86400000);
    }
  }

  return { start, end };
} 